import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { TextButton, TextLink } from '@oneaudi/unified-web-components';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { SYS_BREAKPOINT_LG } from '@oneaudi/unified-web-common';
import { BodyTypeFilterSection } from './BodyTypeFilterSection';
import { CarlineTypeFilterSection } from './CarlineTypeFilterSection';
import { DriveTypeFilterSection } from './DriveTypeFilterSection';
import { useFilterContext } from '../../context/FilterContext';
import { useI18n } from '../../i18n';
import type { NextState } from '../../hooks/useTrackingManager';
import { useTrackingManager } from '../../hooks/useTrackingManager';
import { getDefaultVehicleType, type DisplayedVehicleType } from '../../utils/filterUtils';
import { useUniversalState } from '../../context/UniversalStateContext';

type Props = {
  readonly onCloseResults: () => void;
  readonly onCarlineScroll: () => void;
};

export const FiltersPanel: React.FC<Props> = ({ onCloseResults, onCarlineScroll }) => {
  const sm = parseInt(SYS_BREAKPOINT_LG, 10);
  const isMobile = useMediaQuery(`(max-width: ${sm - 1}px)`);
  const tagSize = process.env.IS_LEGACY ? 'sm' : 'md';

  const {
    selectedCarlineGroups,
    setSelectedCarlineGroups,
    selectedBodyTypes,
    setSelectedBodyTypes,
    selectedVehicleType,
    setSelectedVehicleType,
    filteredCarlines,
    flattenedCarlines,
    carlineGroupsForTags,
    carlinesFilteredByCarlineGroup,
    carlinesFilteredByBodyType,
    hasActiveFilters,
    availableVehicleTypes,
    resetAllFilters,
    bodyOrModelFilterActive,
  } = useFilterContext();

  const i18nViewResults = useI18n('filterShowResults', { value: filteredCarlines.length });
  const viewResultsText = useMemo(
    () => (!isMobile || !filteredCarlines?.length ? null : i18nViewResults),
    [isMobile, filteredCarlines.length, i18nViewResults],
  );
  const resetFilterText = useI18n('filterReset');
  const tracking = useTrackingManager();
  const { translations } = useUniversalState();
  const handleDriveTypeClick = useCallback(
    (vehicleType: DisplayedVehicleType) => {
      const isSelected = selectedVehicleType.includes(vehicleType);
      const newSelected = isSelected
        ? selectedVehicleType.filter((v) => v !== vehicleType)
        : [...selectedVehicleType, vehicleType];

      const nextState: NextState = {
        vehicleTypes: newSelected,
        carlineGroups: selectedCarlineGroups,
        bodyTypes: selectedBodyTypes,
      };

      tracking.trackFilterToggle({
        label: translations?.[`vehicleType.${vehicleType}`] ?? `vehicleType.${vehicleType}`,
        kind: 'vehicleType',
        id: vehicleType,
        next: nextState,
        added: !isSelected,
        ui: 'tag',
        variant: hasActiveFilters ? 'model' : 'group',
      });

      setSelectedVehicleType(newSelected);
      onCarlineScroll();
    },
    [
      selectedVehicleType,
      selectedCarlineGroups,
      selectedBodyTypes,
      translations,
      tracking,
      hasActiveFilters,
      onCarlineScroll,
    ],
  );

  const handleCarlineTypeClick = useCallback(
    (id: string) => {
      const baseVehicleTypes = selectedVehicleType?.length
        ? selectedVehicleType
        : (getDefaultVehicleType() ?? []);
      if (!selectedVehicleType?.length) setSelectedVehicleType(baseVehicleTypes);

      setSelectedCarlineGroups((prev) => {
        const wasSelected = prev.includes(id);
        const nextCarlineGroups = wasSelected ? prev.filter((g) => g !== id) : [...prev, id];

        const nextState: NextState = {
          vehicleTypes: baseVehicleTypes,
          carlineGroups: nextCarlineGroups,
          bodyTypes: selectedBodyTypes,
        };

        tracking.trackFilterToggle({
          label: translations?.[`carlineGroup.${id}`] ?? `carlineGroup.${id}`,
          kind: 'carlineGroup',
          id,
          next: nextState,
          added: !wasSelected,
          ui: 'tag',
          variant: hasActiveFilters ? 'model' : 'group',
        });

        onCarlineScroll();
        return nextCarlineGroups;
      });
    },
    [
      selectedVehicleType,
      selectedBodyTypes,
      getDefaultVehicleType,
      setSelectedVehicleType,
      setSelectedCarlineGroups,
      tracking,
      onCarlineScroll,
    ],
  );

  const handleBodyTypeClick = useCallback(
    (id: string) => {
      const baseVehicleTypes = selectedVehicleType?.length
        ? selectedVehicleType
        : (getDefaultVehicleType() ?? []);
      if (!selectedVehicleType?.length) setSelectedVehicleType(baseVehicleTypes);

      setSelectedBodyTypes((prev) => {
        const wasSelected = prev.includes(id);
        const nextBodyTypes = wasSelected ? prev.filter((t) => t !== id) : [...prev, id];

        const nextState: NextState = {
          vehicleTypes: baseVehicleTypes,
          carlineGroups: selectedCarlineGroups,
          bodyTypes: nextBodyTypes,
        };

        tracking.trackFilterToggle({
          label: translations ? translations[`bodyType.${id}`] : `bodyType.${id}`,
          kind: 'bodyType',
          id,
          next: nextState,
          added: !wasSelected,
          ui: 'tag',
          variant: hasActiveFilters ? 'model' : 'group',
        });

        onCarlineScroll();
        return nextBodyTypes;
      });
    },
    [
      selectedVehicleType,
      selectedCarlineGroups,
      translations,
      tracking,
      onCarlineScroll,
      getDefaultVehicleType,
      setSelectedVehicleType,
      setSelectedBodyTypes,
    ],
  );

  const allAvailableVehicleTypes = () => {
    const types = new Set<string>();
    flattenedCarlines.forEach((carline) => {
      if (carline.vehicleType) {
        types.add(carline.vehicleType);
      }
    });
    return Array.from(types) as DisplayedVehicleType[];
  };

  const resetLink = hasActiveFilters ? (
    <ResetLink
      variant="standalone"
      size="sm"
      onClick={() => {
        tracking.trackResetFilter('filterReset', [
          { id: 'bodyType', values: selectedBodyTypes },
          { id: 'carlineGroup', values: selectedCarlineGroups },
          { id: 'vehicleType', values: selectedVehicleType },
        ]);
        resetAllFilters();
      }}
      icon="refresh"
      iconPosition="leading"
      aria-label="reset"
      data-testid="resetFilter"
    >
      {resetFilterText}
    </ResetLink>
  ) : null;

  return (
    <>
      <DriveTypeFilterSection
        allAvailableVehicleTypes={allAvailableVehicleTypes()}
        availableVehicleTypes={availableVehicleTypes}
        selectedVehicleType={selectedVehicleType}
        onClick={handleDriveTypeClick}
        size={tagSize}
        disableUnavailableTypesOnlyWhenFiltered={bodyOrModelFilterActive}
      />
      <CarlineTypeFilterSection
        carlineGroups={carlineGroupsForTags}
        filteredCarlines={carlinesFilteredByBodyType.map((c) => ({ carlineGroupId: c.group }))}
        selectedCarlineGroups={selectedCarlineGroups}
        onClick={handleCarlineTypeClick}
        size={tagSize}
      />
      <BodyTypeFilterSection
        flattenedCarlines={flattenedCarlines}
        filteredCarlines={carlinesFilteredByCarlineGroup}
        selectedBodyTypes={selectedBodyTypes}
        onClick={handleBodyTypeClick}
        size={tagSize}
      />

      {(resetLink || (isMobile && viewResultsText)) && (
        <Actions>
          {resetLink}
          {isMobile && viewResultsText && (
            <TextButton
              aria-label={viewResultsText}
              onClick={onCloseResults}
              size="sm"
              variant="primary"
              data-testid="closeDialog"
            >
              {viewResultsText}
            </TextButton>
          )}
        </Actions>
      )}
    </>
  );
};

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.fixed200};
  padding-top: ${({ theme }) => theme.spacing.fixed450};
  & > * {
    width: 100%;
    display: inline-flex;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    flex-direction: row;
    & > * {
      width: auto;
      flex: 0 1 auto;
    }
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding-top: ${({ theme }) => theme.spacing.fixed300};
  }
`;

const ResetLink = styled(TextLink)`
  align-self: center;
  justify-content: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    order: 2;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    order: 0;
    justify-content: flex-start;
  }
`;
